import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Minus, Plus, X } from "lucide-react";
import { api, fileUrl } from "../lib/api";
import { useCart } from "../context/CartContext";

export default function Cart() {
    const { items, setQty, remove, subtotal, clear } = useCart();
    const [email, setEmail] = useState("");
    const [busy, setBusy] = useState(false);

    const checkout = async () => {
        if (items.length === 0) return;
        setBusy(true);
        try {
            const payload = {
                items: items.map((i) => ({ print_id: i.print_id, quantity: i.quantity, size_label: i.size_label || undefined })),
                origin_url: window.location.origin,
                email: email || undefined,
            };
            const r = await api.post("/payments/checkout", payload);
            window.location.href = r.data.checkout_url;
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Checkout failed");
            setBusy(false);
        }
    };

    return (
        <div className="relative z-10 pt-24 sm:pt-32 pb-24" data-testid="cart-page">
            <div className="max-w-[1200px] mx-auto px-5 sm:px-10 lg:px-16">
                <div className="mb-10 sm:mb-12">
                    <div className="label-caps text-[#8C3A3A] mb-4">Checkout</div>
                    <h1 className="font-serif-display text-5xl sm:text-6xl font-light tracking-tighter text-[#1A1A1A]">
                        Your Cart
                    </h1>
                </div>

                {items.length === 0 ? (
                    <div className="py-24 text-center border-t border-b border-black/[0.06]">
                        <div className="font-serif-display text-3xl text-[#1A1A1A] mb-4 italic">Your cart is quiet.</div>
                        <Link to="/gallery" className="inline-block mt-4 border border-black/20 text-[#1A1A1A] px-8 py-3 rounded-full text-sm tracking-wide hover:bg-black/5 transition-colors" data-testid="empty-cart-shop-btn">
                            Explore the gallery
                        </Link>
                    </div>
                ) : (
                    <div className="grid lg:grid-cols-[1fr_400px] gap-10 lg:gap-16">
                        <div className="space-y-8">
                            {items.map((it) => (
                                <div key={`${it.print_id}::${it.size_label || ""}`} className="flex gap-4 sm:gap-6 pb-8 border-b border-black/[0.06]" data-testid={`cart-item-${it.print_id}`}>
                                    <img src={fileUrl(it.image)} alt={it.title} className="w-20 h-28 sm:w-32 sm:h-40 object-cover bg-[#F0EDE5] shrink-0" />
                                    <div className="flex-1 min-w-0">
                                        <div className="label-caps mb-2">{it.medium}{it.size_label && ` · ${it.size_label}`}</div>
                                        <div className="font-serif-display text-xl sm:text-2xl text-[#1A1A1A]">{it.title}</div>
                                        <div className="text-[#5A5A5A] text-sm mt-2">A${it.price.toFixed(2)}</div>
                                        <div className="mt-4 flex flex-wrap items-center gap-4">
                                            <div className="inline-flex items-center border border-black/15">
                                                <button onClick={() => setQty(it.print_id, it.quantity - 1, it.size_label)} className="p-2 text-[#5A5A5A] hover:text-[#1A1A1A]" data-testid={`decr-${it.print_id}`}><Minus className="w-3 h-3" /></button>
                                                <div className="px-4 text-sm text-[#1A1A1A]">{it.quantity}</div>
                                                <button onClick={() => setQty(it.print_id, it.quantity + 1, it.size_label)} className="p-2 text-[#5A5A5A] hover:text-[#1A1A1A]" data-testid={`incr-${it.print_id}`}><Plus className="w-3 h-3" /></button>
                                            </div>
                                            <button onClick={() => remove(it.print_id, it.size_label)} className="text-[#8A8A8A] hover:text-[#8C3A3A] text-xs tracking-[0.2em] uppercase inline-flex items-center gap-1" data-testid={`remove-${it.print_id}`}>
                                                <X className="w-3 h-3" /> Remove
                                            </button>
                                        </div>
                                    </div>
                                    <div className="hidden sm:block text-[#1A1A1A] font-serif-display text-xl">A${(it.price * it.quantity).toFixed(2)}</div>
                                </div>
                            ))}
                        </div>

                        <div className="lg:sticky lg:top-32 h-fit bg-[#F0EDE5] p-6 sm:p-8 border border-black/[0.06]">
                            <div className="label-caps mb-6 text-[#1A1A1A]">Order Summary</div>

                            <div className="mb-6">
                                <label className="label-caps block mb-2">Email (optional, for receipt)</label>
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="underline-input"
                                    placeholder="you@example.com"
                                    data-testid="cart-email-input"
                                />
                            </div>

                            <div className="space-y-3 pb-6 border-b border-black/[0.06]">
                                <div className="flex justify-between text-sm">
                                    <span className="text-[#5A5A5A]">Subtotal</span>
                                    <span className="text-[#1A1A1A]" data-testid="cart-subtotal">A${subtotal.toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between text-sm">
                                    <span className="text-[#5A5A5A]">Shipping & tax</span>
                                    <span className="text-[#5A5A5A] text-xs">Calculated at checkout</span>
                                </div>
                            </div>

                            <div className="flex justify-between items-baseline my-6">
                                <span className="label-caps text-[#1A1A1A]">Total</span>
                                <span className="font-serif-display text-3xl text-[#1A1A1A]">A${subtotal.toFixed(2)}</span>
                            </div>

                            <button
                                onClick={checkout}
                                disabled={busy}
                                className="w-full bg-[#1A1A1A] text-[#FAFAF7] px-8 py-4 rounded-full text-sm tracking-wide font-medium hover:bg-[#8C3A3A] hover:text-white disabled:opacity-50 transition-colors"
                                data-testid="checkout-btn"
                            >
                                {busy ? "Redirecting to Stripe..." : "Proceed to Checkout"}
                            </button>
                            <button onClick={clear} className="w-full mt-4 text-xs tracking-[0.2em] uppercase text-[#8A8A8A] hover:text-[#1A1A1A]" data-testid="clear-cart-btn">Clear cart</button>
                            <p className="mt-6 text-xs text-[#8A8A8A] leading-relaxed">
                                Secure checkout via Stripe. All prints are dropshipped worldwide from the studio's fulfillment partner.
                            </p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
